import React, { useMemo, useState } from 'react'
import T from 'prop-types'
import {
  Box,
  ListItemText,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded'
import { useTranslation } from 'react-i18next'

import ConfirmationDialog from 'components/atoms/ConfirmationDialog'
import InputLabel from 'components/atoms/InputLabel'
import SelectScrollable from 'components/atoms/SelectScrollable'

const SELECT_PARTNER = 'sp'

export default function AssignToDialog({
  partners = [],
  openConfirmationDialog,
  handleCloseConfirmationDialog,
  handleConfirmConfirmationDialog,
  readOnly,
}) {
  const { t } = useTranslation()

  const [localSelectValue, setLocalSelectValue] = useState(SELECT_PARTNER)
  const [localTextareaValue, setLocalTextareaValue] = useState('')

  const partnersOptions = useMemo(
    () => [
      {
        label: t('assignment.car-details.assignToDialog.noPartner'),
        value: '',
      },
      ...partners.map(
        (partner) =>
          partner && { label: partner.partnerName, value: partner.partnerId }
      ),
    ],
    [partners, t]
  )

  const handleReset = () => {
    if (localSelectValue !== SELECT_PARTNER) {
      setLocalSelectValue(SELECT_PARTNER)
    }

    if (localTextareaValue !== '') {
      setLocalTextareaValue('')
    }
  }
  const handleChangeSelectLocal = (event, selected) => {
    setLocalSelectValue(selected.props.value)
  }

  const handleChangeTextareaLocal = (event) => {
    setLocalTextareaValue(event.target.value)
  }

  const handleClose = () => {
    handleCloseConfirmationDialog()
    handleReset()
  }

  const handleConfirm = () => {
    handleConfirmConfirmationDialog(
      localSelectValue || null,
      localTextareaValue
    )
    handleReset()
  }

  return (
    <ConfirmationDialog
      title={t('assignment.car-details.assignToDialog.title')}
      content={
        <>
          <Stack direction="row" alignItems="center">
            <WarningAmberRoundedIcon
              color="secondary"
              sx={{ bgcolor: 'error.main', borderRadius: 1 }}
            />
            <Box pr={1} />
            <Typography variant="body2" color="textPrimary">
              {t('assignment.car-details.assignToDialog.advice')}
            </Typography>
          </Stack>

          <Box pb={4} />

          <InputLabel label={t('common.form.partner')} />
          <SelectScrollable
            size="small"
            value={localSelectValue}
            onChange={handleChangeSelectLocal}
            disabled={readOnly}
            displayEmpty
            renderValue={(selected) => {
              return (
                partnersOptions.find((partner) => partner.value === selected)
                  ?.label ||
                t('assignment.car-details.assignToDialog.partnerPlaceholder')
              )
            }}
            fullWidth
          >
            <MenuItem disabled value={SELECT_PARTNER}>
              <ListItemText
                primary={t(
                  'assignment.car-details.assignToDialog.partnerPlaceholder'
                )}
              />
            </MenuItem>

            {partnersOptions.map((option) => {
              return (
                <MenuItem key={option.value} value={option.value}>
                  <ListItemText primary={option.label} />
                </MenuItem>
              )
            })}
          </SelectScrollable>
          <Box pb={2} />
          <InputLabel
            label={t('assignment.car-details.assignToDialog.reason')}
          />
          <TextField
            placeholder={t(
              'assignment.car-details.assignToDialog.reasonPlaceholder'
            )}
            value={localTextareaValue}
            onChange={handleChangeTextareaLocal}
            multiline
            fullWidth
            rows={2}
            rowsmax={4}
            disabled={readOnly}
          />
        </>
      }
      contentComponent={Box}
      confirmLabel={t('assignment.car-details.assignToDialog.confirm')}
      open={openConfirmationDialog}
      readOnly={
        !partnersOptions.find(
          (partner) => partner.value === localSelectValue
        ) ||
        !localTextareaValue.trim() ||
        readOnly
      }
      handleClose={handleClose}
      handleSubmit={handleConfirm}
    />
  )
}

AssignToDialog.propTypes = {
  partners: T.array,
  openConfirmationDialog: T.bool,
  handleCloseConfirmationDialog: T.func,
  handleConfirmConfirmationDialog: T.func,
  readOnly: T.bool,
}
